/**
 * Single source of truth for workspace tab type icons.
 *
 * The same icon a workspace type appears with in the main toolbar / "More"
 * dropdown / context menu MUST appear on its tab strip and on the start-page
 * recent-tabs list — otherwise users see the same workspace type rendered
 * with different icons in different places.
 */

import {
  Home,
  FileCode,
  FileText,
  Table2,
  BookOpen,
  FolderGit2,
  GitCompare,
  TableProperties,
  LayoutGrid,
  Network,
  Shield,
  Settings,
  FileSearch,
  Users,
  HardDrive,
  DatabaseBackup,
  Activity,
  Bug,
  Bot,
  Package,
  Info,
  BarChart3,
  ArrowRightLeft,
  LayoutDashboard,
  Map,
  FileInput,
  Lock,
  History,
  Cloud,
  Container,
  MessageSquare,
  FolderTree,
  FolderOpen,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { Tab, WorkspaceType, StorageExplorerMode } from '../types/workspaceTypes';
import { isQueryTab, isStorageExplorerTab } from '../types/workspaceTypes';

/**
 * Per-mode Storage Explorer icons (spec §6.1 "Tab icon: per mode"). The type
 * icon (`getTabTypeIcon`) is the generic cloud because it only sees the type;
 * `getTabIcon` refines it once the tab's mode is known.
 */
export function getStorageExplorerModeIcon(mode: StorageExplorerMode): LucideIcon {
  switch (mode) {
    case 'blobs':
      return Container;
    case 'entities':
      return Table2;
    case 'messages':
      return MessageSquare;
    case 'files':
      return FolderOpen;
    case 'hns':
      return FolderTree;
  }
}

export function getTabTypeIcon(type: WorkspaceType): LucideIcon {
  switch (type) {
    case 'start-page':
      return Home;
    case 'query':
      return FileCode;
    case 'table-explorer':
    case 'table-editor':
      return Table2;
    case 'table-designer':
      return Settings;
    case 'plan':
      return FileSearch;
    case 'security':
      return Shield;
    case 'schema-compare':
      return GitCompare;
    case 'data-compare':
      return TableProperties;
    case 'database-blueprint':
      return FolderGit2;
    case 'dependency-viewer':
      return Network;
    case 'schema-overview':
      return LayoutGrid;
    case 'spatial-explorer':
      return Map;
    case 'data-profile':
      return BarChart3;
    case 'notebook':
      return BookOpen;
    case 'session-browser':
      return Users;
    case 'tablespace-manager':
    case 'filegroup-manager':
      return HardDrive;
    case 'oracle-object-detail':
      return Info;
    case 'data-pump':
      return DatabaseBackup;
    case 'instance-monitor':
      return Activity;
    case 'pl-sql-debugger':
      return Bug;
    case 'job-scheduler':
      return Bot;
    case 'plsql-editor':
      return FileCode;
    case 'package-explorer':
      return Package;
    case 'cross-engine-migrate':
      return ArrowRightLeft;
    case 'dashboard':
      return LayoutDashboard;
    case 'data-import':
      return FileInput;
    case 'deadlock-analyzer':
      return Lock;
    case 'query-store':
      return History;
    case 'storage-explorer':
      return Cloud;
    default:
      return FileCode;
  }
}

export function getTabIcon(tab: Tab): LucideIcon {
  // File-backed query tabs (the user opened a saved .sql file) render as a
  // document, not as the generic "new query" file-code glyph.
  if (isQueryTab(tab) && !!tab.content.filePath) return FileText;
  if (isStorageExplorerTab(tab)) return getStorageExplorerModeIcon(tab.content.mode);
  return getTabTypeIcon(tab.type);
}
